import { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  LinearProgress,
  Paper,
  TextField,
  Toolbar,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

export default function SiteAdminPage() {
  const { user, login, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [imageBase, setImageBase] = useState('');
  const [subs, setSubs] = useState<string[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [find, setFind] = useState('');
  const [replace, setReplace] = useState('');
  const [preview, setPreview] = useState<{ flairCount: number; imageCount: number } | null>(null);

  type MismatchRow = {
    sub_name: string;
    flair_code: string;
    flair_name: string;
    flair_text: string | null;
    flair_abbreviated: string | null;
    image_name: string;
    image_id: number;
  };
  type MismatchGroup = {
    flairCodeNormalized: string;
    distinctSubCount: number;
    distinctTextVariants: number;
    rows: MismatchRow[];
  };
  const [mismatchData, setMismatchData] = useState<{
    mismatchCount: number;
    mismatches: MismatchGroup[];
    fieldsCompared: string[];
    note?: string;
  } | null>(null);
  const [discoveryBusy, setDiscoveryBusy] = useState(false);

  type LandingRow = {
    subreddit: string;
    userCount: number;
    hideFromLanding: boolean;
  };
  const [landingRows, setLandingRows] = useState<LandingRow[]>([]);
  const [landingLoading, setLandingLoading] = useState(false);
  const [landingSaving, setLandingSaving] = useState<string | null>(null);
  const [manualSub, setManualSub] = useState('');

  useEffect(() => {
    if (!user) {
      setAllowed(null);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/site-admin/me`, {
          headers: { Authorization: `Bearer ${user.username}` },
        });
        setAllowed(r.ok);
      } catch {
        setAllowed(false);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (allowed !== true || !user) return;
    let cancelled = false;
    (async () => {
      setLandingLoading(true);
      try {
        const r = await fetch(`${API_BASE_URL}/site-admin/landing-visibility`, {
          headers: { Authorization: `Bearer ${user.username}` },
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Failed to load landing visibility');
        if (!cancelled) setLandingRows(d.subreddits || []);
      } catch (e) {
        if (!cancelled) {
          setLog((prev) => [
            `Landing visibility: ${e instanceof Error ? e.message : String(e)}`,
            ...prev,
          ]);
        }
      } finally {
        if (!cancelled) setLandingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, user]);

  const setLandingHide = async (
    subreddit: string,
    hideFromLanding: boolean
  ): Promise<boolean> => {
    if (!user) return false;
    setLandingSaving(subreddit);
    try {
      const r = await fetch(
        `${API_BASE_URL}/site-admin/landing-visibility/${encodeURIComponent(subreddit)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.username}`,
          },
          body: JSON.stringify({ hideFromLanding }),
        }
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to save');
      setLandingRows((prev) => {
        const i = prev.findIndex((row) => row.subreddit === subreddit);
        if (i >= 0) {
          const next = [...prev];
          next[i] = { ...next[i], hideFromLanding: d.hideFromLanding };
          return next;
        }
        return prev;
      });
      return true;
    } catch (e) {
      setLog((prev) => [
        ...prev,
        `[${subreddit}] ${e instanceof Error ? e.message : String(e)}`,
      ]);
      return false;
    } finally {
      setLandingSaving(null);
    }
  };

  const applyManualLanding = async (hideFromLanding: boolean) => {
    if (!user) return;
    const sub = manualSub.trim().toLowerCase().replace(/^r\//, '');
    if (!sub) {
      setLog((prev) => [...prev, 'Enter a subreddit name (e.g. mysub or r/mysub).']);
      return;
    }
    const ok = await setLandingHide(sub, hideFromLanding);
    if (!ok) return;
    setManualSub('');
    setLog((prev) => [
      ...prev,
      hideFromLanding
        ? `Marked /r/${sub} hidden from www landing. (Subs not in the popularity cache only appear in this table after they have a cache row.)`
        : `Marked /r/${sub} visible on www landing.`,
    ]);
  };

  const loadSubsForSource = async () => {
    if (!user || !imageBase.trim()) return;
    setBusy(true);
    setLog([]);
    try {
      const b = encodeURIComponent(imageBase.replace(/\.png$/i, '').trim());
      const r = await fetch(`${API_BASE_URL}/site-admin/image-source-subs/${b}`, {
        headers: { Authorization: `Bearer ${user.username}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      const list = d.subreddits || [];
      setSubs(list);
      const sel: Record<string, boolean> = {};
      list.forEach((s: string) => {
        sel[s] = true;
      });
      setSelected(sel);
      setLog([`Found ${list.length} sub(s) with image_source matching "${d.imageSource}".`]);
    } catch (e) {
      setLog([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  };

  const runBulkUpload = async () => {
    if (!user || !file) return;
    const chosen = subs.filter((s) => selected[s]);
    if (chosen.length === 0) {
      setLog(['Select at least one subreddit.']);
      return;
    }
    setBusy(true);
    const lines: string[] = [];
    for (const sub of chosen) {
      try {
        const fd = new FormData();
        fd.append('images', file);
        const r = await fetch(`${API_BASE_URL}/moderator/${sub}/import-images`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${user.username}` },
          body: fd,
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || d.details || r.statusText);
        const n = d.results?.uploaded?.length ?? d.uploaded?.length ?? 0;
        lines.push(`[${sub}] CDN upload: ${n} file(s).`);
        const baseName = file.name.replace(/\.[^.]+$/, '');
        const push = await fetch(`${API_BASE_URL}/moderator/${sub}/push-emojis-by-names`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.username}`,
          },
          body: JSON.stringify({ imageNames: [baseName] }),
        });
        const pj = await push.json().catch(() => ({}));
        if (!push.ok && push.status !== 202) {
          lines.push(`[${sub}] Emoji push skipped: ${pj.error || push.status}`);
        } else {
          const { pollPushEmojisByNamesUntilDone } = await import('../utils/pushEmojisByNames');
          const finalResult = await pollPushEmojisByNamesUntilDone(API_BASE_URL, sub, user.username, {});
          lines.push(
            `[${sub}] Emojis: ${finalResult?.emojis_uploaded ?? '?'} (${finalResult?.success === false ? 'check errors' : 'ok'})`
          );
          const cr = await fetch(
            `${API_BASE_URL}/moderator/${sub}/classes-for-images?names=${encodeURIComponent(baseName)}`,
            { headers: { Authorization: `Bearer ${user.username}` } }
          );
          const cd = await cr.json();
          const classes = cd.classes || [];
          if (classes.length > 0) {
            const pr = await fetch(`${API_BASE_URL}/moderator/${sub}/post-emoji-refresh`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.username}`,
              },
              body: JSON.stringify({ classNames: classes }),
            });
            const prj = await pr.json().catch(() => ({}));
            lines.push(
              `[${sub}] Spritesheets + wiki: ${pr.ok ? 'ok' : prj.error || pr.status}`
            );
          } else {
            lines.push(`[${sub}] No classes resolved for spritesheet refresh.`);
          }
        }
      } catch (e) {
        lines.push(`[${sub}] Error: ${e instanceof Error ? e.message : String(e)}`);
      }
    }
    setLog(lines);
    setBusy(false);
  };

  const runPreview = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/site-admin/text-standardize/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify({ find, replace }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Preview failed');
      setPreview({ flairCount: d.flairCount, imageCount: d.imageCount });
      setLog([
        `Preview: ${d.flairCount} flair row(s), ${d.imageCount} image row(s) would change.`,
        'Review API response in Network tab for full diff, or apply below.',
      ]);
    } catch (e) {
      setLog([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  };

  const runMismatchDiscovery = async () => {
    if (!user) return;
    setDiscoveryBusy(true);
    setMismatchData(null);
    try {
      const r = await fetch(`${API_BASE_URL}/site-admin/text-mismatch-discovery`, {
        headers: { Authorization: `Bearer ${user.username}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Discovery failed');
      setMismatchData(d);
      setLog([
        `Mismatch discovery: ${d.mismatchCount} flair_code value(s) with cross-sub text differences.`,
        d.note || '',
      ].filter(Boolean));
    } catch (e) {
      setLog([e instanceof Error ? e.message : String(e)]);
    } finally {
      setDiscoveryBusy(false);
    }
  };

  const downloadMismatchJson = () => {
    if (!mismatchData) return;
    const blob = new Blob([JSON.stringify(mismatchData, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `flair-code-text-mismatches-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const runApply = async () => {
    if (!user || !window.confirm('Apply REPLACE to all matching flair + image rows across all subs?')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/site-admin/text-standardize/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify({ find, replace }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Apply failed');
      setLog([
        `Applied. Flair rows updated: ${d.flairRowsUpdated}, image rows: ${d.imageRowsUpdated}.`,
        `Master sheet rebuild: ${JSON.stringify(d.masterSheetRebuild)}`,
      ]);
    } catch (e) {
      setLog([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info">Log in with Reddit to continue.</Alert>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => login()}>
          Login
        </Button>
      </Container>
    );
  }

  if (allowed === false) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Site admin only (bakonydraco).</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/')}>
          Home
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Flair Wizard site admin
          </Typography>
          <Button color="inherit" onClick={() => navigate('/')}>
            Home
          </Button>
          <Button color="inherit" onClick={() => logout()}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {allowed === null && <LinearProgress sx={{ mb: 2 }} />}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Bulk image by filename (image_source)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the image basename (e.g. <code>alabama</code> for <code>alabama.png</code>). Loads subs that reference it, then upload one file to push to selected subs (CDN → emojis → spritesheets → wiki).
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              label="Image base name"
              value={imageBase}
              onChange={(e) => setImageBase(e.target.value)}
            />
            <Button variant="outlined" onClick={loadSubsForSource} disabled={busy}>
              Find subs
            </Button>
            <Button variant="contained" component="label" disabled={busy}>
              Select PNG
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
            {file && (
              <Typography variant="body2">
                {file.name}
              </Typography>
            )}
          </Box>
          {subs.length > 0 && (
            <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', p: 1, mb: 2 }}>
              {subs.map((s) => (
                <FormControlLabel
                  key={s}
                  control={
                    <Checkbox
                      checked={!!selected[s]}
                      onChange={() => setSelected((prev) => ({ ...prev, [s]: !prev[s] }))}
                    />
                  }
                  label={`/r/${s}`}
                />
              ))}
            </Box>
          )}
          <Button variant="contained" color="secondary" onClick={runBulkUpload} disabled={busy || !file || subs.length === 0}>
            {busy ? 'Working…' : 'Upload & push to selected'}
          </Button>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            WWW landing page — subreddit list
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Subreddits shown on the main site (popular list) come from the popularity cache. Check{' '}
            <strong>Hide from www</strong> to omit a sub (e.g. NSFW or not fully set up). Requires DB migration{' '}
            <code>add_subreddit_landing_visibility.sql</code>.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={landingLoading || !user}
              onClick={() => {
                if (!user) return;
                setLandingLoading(true);
                fetch(`${API_BASE_URL}/site-admin/landing-visibility`, {
                  headers: { Authorization: `Bearer ${user.username}` },
                })
                  .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
                  .then(({ ok, d }) => {
                    if (!ok) throw new Error(d.error || 'Failed');
                    setLandingRows(d.subreddits || []);
                  })
                  .catch((e) =>
                    setLog((prev) => [
                      ...prev,
                      `Landing visibility reload: ${e instanceof Error ? e.message : String(e)}`,
                    ])
                  )
                  .finally(() => setLandingLoading(false));
              }}
            >
              {landingLoading ? 'Loading…' : 'Reload list'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              label="Other subreddit (manual)"
              placeholder="mysub"
              value={manualSub}
              onChange={(e) => setManualSub(e.target.value)}
              helperText="Hide or show by name if it is not in the table yet"
            />
            <Button
              variant="outlined"
              size="small"
              disabled={!!landingSaving || busy}
              onClick={() => applyManualLanding(true)}
            >
              Hide
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!!landingSaving || busy}
              onClick={() => applyManualLanding(false)}
            >
              Show
            </Button>
          </Box>
          {landingLoading && landingRows.length === 0 ? (
            <LinearProgress sx={{ my: 2 }} />
          ) : (
            <TableContainer sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Subreddit</TableCell>
                    <TableCell align="right">Flaired users</TableCell>
                    <TableCell align="center">Hide from www</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {landingRows.map((row) => (
                    <TableRow key={row.subreddit}>
                      <TableCell>
                        <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                          /r/{row.subreddit}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{row.userCount}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={row.hideFromLanding}
                          disabled={landingSaving === row.subreddit || busy}
                          onChange={(_, checked) => setLandingHide(row.subreddit, checked)}
                          inputProps={{ 'aria-label': `Hide ${row.subreddit} from www` }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!landingLoading && landingRows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No rows (empty popularity cache or migration not applied).
            </Typography>
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Cross-sub flair_code text mismatch discovery
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Finds <code>flair_code</code> values that appear on <strong>at least two subreddits</strong> where any of{' '}
            <strong>flair name</strong>, <strong>flair text</strong>, <strong>flair abbreviated</strong>, or{' '}
            <strong>image name</strong> differ. <code>image_description</code> is excluded. Uses active image + active
            flair rows only.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <Button variant="contained" onClick={runMismatchDiscovery} disabled={discoveryBusy || busy}>
              {discoveryBusy ? 'Scanning…' : 'Run discovery'}
            </Button>
            {mismatchData && mismatchData.mismatchCount > 0 && (
              <Button variant="outlined" onClick={downloadMismatchJson}>
                Download JSON
              </Button>
            )}
          </Box>
          {mismatchData && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {mismatchData.mismatchCount === 0
                ? 'No mismatches found (same four fields everywhere per flair_code across subs).'
                : `${mismatchData.mismatchCount} flair_code group(s) with differences. Compared: ${(mismatchData.fieldsCompared || []).join(', ')}.`}
            </Typography>
          )}
          {mismatchData && mismatchData.mismatches.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              {mismatchData.mismatches.map((g: MismatchGroup) => (
                <Box key={g.flairCodeNormalized}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontFamily: 'monospace' }}>
                    flair_code (normalized): {g.flairCodeNormalized}{' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      — {g.distinctSubCount} subs, {g.distinctTextVariants} distinct text variants
                    </Typography>
                  </Typography>
                  <TableContainer sx={{ maxHeight: 320, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>sub</TableCell>
                          <TableCell>flair_code (raw)</TableCell>
                          <TableCell>flair_name</TableCell>
                          <TableCell>flair_text</TableCell>
                          <TableCell>flair_abbreviated</TableCell>
                          <TableCell>image_name</TableCell>
                          <TableCell align="right">image_id</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {g.rows.map((row: MismatchRow) => (
                          <TableRow key={`${row.sub_name}-${row.image_id}`}>
                            <TableCell>{row.sub_name}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.flair_code}</TableCell>
                            <TableCell>{row.flair_name}</TableCell>
                            <TableCell>{row.flair_text ?? ''}</TableCell>
                            <TableCell>{row.flair_abbreviated ?? ''}</TableCell>
                            <TableCell>{row.image_name}</TableCell>
                            <TableCell align="right">{row.image_id}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Text standardize (substring)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Replaces <code>find</code> with <code>replace</code> in flair (name, text, abbreviated) and image (name, description) across <strong>all</strong> subs. Then rebuilds <code>master_sheet</code> per affected sub. Use preview counts first; some intentional variants (e.g. Lumberjacks vs Lumberjills) will also change unless you narrow scope later.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Find"
            value={find}
            onChange={(e) => setFind(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Replace with"
            value={replace}
            onChange={(e) => setReplace(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="outlined" onClick={runPreview} disabled={busy || !find}>
              Preview counts
            </Button>
            <Button variant="contained" color="warning" onClick={runApply} disabled={busy || !find}>
              Apply
            </Button>
          </Box>
          {preview && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Last preview: {preview.flairCount} flair row(s), {preview.imageCount} image row(s).
            </Typography>
          )}
        </Paper>

        {log.length > 0 && (
          <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.100' }}>
            <Typography variant="subtitle2" gutterBottom>
              Log
            </Typography>
            {log.map((line, i) => (
              <Typography key={i} variant="body2" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {line}
              </Typography>
            ))}
          </Paper>
        )}
      </Container>
    </Box>
  );
}
